import React, { useState, useCallback } from 'react';
import { Plus, EnvelopeSimple, ChatCircle } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { PostCallActionCard } from 'src/components/PostCallActionCard';
import { PostCallActionModal } from 'src/components/PostCallActionModal';
import { Agent, PostCallFinalizedAction } from 'src/interfaces/agent.interface';
import { PostCallConditionsMode } from './conditions/types';

type PostCallActionsSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};

export const PostCallActionsSection: React.FC<PostCallActionsSectionProps> = ({
  agent,
  setAgent,
  loading = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAction, setEditingAction] =
    useState<PostCallFinalizedAction | null>(null);

  const isDisabled = loading || (!agent.user_id && !!agent.id);
  const postCallMode = agent.post_call_prompt?.trim()
    ? PostCallConditionsMode.PROMPT
    : PostCallConditionsMode.DATA_EXTRACTION;

  const actions = agent.post_call_actions || [];

  const handleAddAction = useCallback(() => {
    setEditingAction(null);
    setModalOpen(true);
  }, []);

  const handleEditAction = useCallback((action: PostCallFinalizedAction) => {
    setEditingAction(action);
    setModalOpen(true);
  }, []);

  const handleSaveAction = useCallback(
    (savedAction: PostCallFinalizedAction) => {
      setAgent((prev) => {
        const currentActions = prev.post_call_actions || [];
        const existingIndex = currentActions.findIndex(
          (a) => a.id === savedAction.id,
        );

        let updatedActions: PostCallFinalizedAction[];
        if (existingIndex >= 0) {
          updatedActions = [...currentActions];
          updatedActions[existingIndex] = savedAction;
        } else {
          updatedActions = [...currentActions, savedAction];
        }

        return {
          ...prev,
          post_call_actions: updatedActions,
        };
      });
    },
    [setAgent],
  );

  const handleDeleteAction = useCallback(
    (id: string) => {
      setAgent((prev) => {
        const currentActions = prev.post_call_actions || [];
        return {
          ...prev,
          post_call_actions: currentActions.filter((a) => a.id !== id),
        };
      });
    },
    [setAgent],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingAction(null);
  }, []);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h4>Post-Call Messaging</h4>
          <p className="text-neutral-400 text-sm">
            Configure post-call email and SMS messaging for your agent.
          </p>
        </div>
        <Button
          data-testid="button-add-action"
          onClick={handleAddAction}
          disabled={isDisabled}
          size="small"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Message
        </Button>
      </div>

      {actions.length === 0 ? (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-8 text-center space-y-4">
          <div className="flex justify-center gap-4">
            <div className="p-3 rounded-full bg-neutral-100">
              <EnvelopeSimple className="w-5 h-5 text-neutral-400" />
            </div>
            <div className="p-3 rounded-full bg-neutral-100">
              <ChatCircle className="w-5 h-5 text-neutral-400" />
            </div>
          </div>
          <div>
            <h3 className="text-body font-medium text-primary-body mb-1">
              No messages configured
            </h3>
            <p className="text-description text-neutral-400">
              You can create rules to conditionally send emails or SMS messages
              after each call.
            </p>
          </div>
          <Button
            variant="outlined"
            color="primary"
            data-testid="button-add-first-action"
            onClick={handleAddAction}
            disabled={isDisabled}
            size="small"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Message
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {actions.map((action) => (
            <PostCallActionCard
              key={action.id}
              action={action}
              onClick={() => handleEditAction(action)}
              disabled={isDisabled}
            />
          ))}
        </div>
      )}

      <PostCallActionModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        action={editingAction}
        onSave={handleSaveAction}
        onDelete={handleDeleteAction}
        disabled={isDisabled}
        postCallMode={postCallMode}
        postCallAnalysisItems={agent.post_call_analysis_items || []}
      />
    </div>
  );
};
